/**
 * SEO Nature & Gîtes — configuration et helpers (priorité FR / Google).
 * Mots-clés cibles : autonomie collective, autonomie collective Nièvre,
 * tiers-lieu rural, gîtes autonomes, SCIC, Bourgogne.
 */

export const SITE_URL = 'https://n-g.fr'
export const SITE_NAME = 'Nature & Gîtes'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logos/png/logo_512x512.png`
export const DEFAULT_LOCALE = 'fr_FR'

/** Réseaux pour sameAs (schema.org) */
export const SOCIAL_PROFILES = [
  'https://www.youtube.com/@naturegites',
  'https://www.tiktok.com/@nature_gites',
  'https://www.instagram.com/nature_gites',
  'https://www.facebook.com/share/12LLKoFyNJ6/',
  'https://x.com/reve_jean',
  'https://t.me/+eXLQZxEkmfs0MTU0',
  'https://www.linkedin.com/in/jean-réveillac-373579188',
  'https://discord.gg/gAskaENj',
  'https://www.bonzai.pro/nature_gites_Gms',
]

export const DEFAULT_KEYWORDS = [
  'autonomie collective',
  'autonomie collective Nièvre',
  'autonomie collective Bourgogne',
  'tiers-lieu rural',
  'tiers-lieu Nièvre',
  'gîtes autonomes',
  'gîtes ruraux Nièvre',
  'SCIC Nature & Gîtes',
  'développement durable rural',
  'permaculture Nièvre',
  'vergers agroécologiques',
  'forêt durable',
  'coopérative d’intérêt collectif',
  'résilience territoriale',
  'autonomie énergétique',
].join(', ')

/**
 * Fiches SEO par route (FR, indexables).
 * title ≤ ~60 car., description ≤ ~155–160 car. idéalement.
 */
export const PAGE_SEO = {
  home: {
    title: 'Autonomie collective en Nièvre | Nature & Gîtes',
    description:
      'Nature & Gîtes : autonomie collective en Nièvre. Tiers-lieu rural, gîtes autonomes, vergers agroécologiques, forêt durable et SCIC au service du territoire.',
    path: '/',
    keywords: DEFAULT_KEYWORDS,
  },
  contact: {
    title: 'Contact — Autonomie collective Nièvre | Nature & Gîtes',
    description:
      'Contactez Nature & Gîtes (SCIC) en Nièvre : partenariats, informations sur l’autonomie collective, gîtes autonomes et projet de tiers-lieu rural.',
    path: '/contact',
  },
  media: {
    title: 'Média — Autonomie collective & projet rural | Nature & Gîtes',
    description:
      'Vidéos, photos et réseaux de Nature & Gîtes : suivez le projet d’autonomie collective en Nièvre, les gîtes autonomes et la vie du tiers-lieu.',
    path: '/media',
  },
  partners: {
    title: 'Partenaires — Réseau autonomie collective | Nature & Gîtes',
    description:
      'Partenaires et affiliations de Nature & Gîtes. Rejoignez le réseau de l’autonomie collective en Nièvre et collaborez avec notre SCIC rurale.',
    path: '/partenaires',
  },
  newsletter: {
    title: 'Newsletter — Actualités autonomie collective | Nature & Gîtes',
    description:
      'Inscrivez-vous à la newsletter Nature & Gîtes : avancées du tiers-lieu, autonomie collective en Nièvre, gîtes, vergers et vie de la coopérative.',
    path: '/newsletter',
  },
  shop: {
    title: 'Boutique solidaire | Nature & Gîtes — Autonomie collective',
    description:
      'Boutique Nature & Gîtes : stickers et produits solidaires pour soutenir l’autonomie collective en Nièvre et le tiers-lieu rural SCIC.',
    path: '/boutique',
  },
  legal: {
    title: 'Mentions légales | Nature & Gîtes',
    description:
      'Mentions légales du site Nature & Gîtes (SCIC) — projet d’autonomie collective en Nièvre.',
    path: '/mentions-legales',
  },
  cgv: {
    title: 'CGV | Nature & Gîtes',
    description:
      'Conditions générales de vente de la boutique Nature & Gîtes — SCIC autonomie collective en Nièvre.',
    path: '/cgv',
  },
  shipping: {
    title: 'Livraison | Nature & Gîtes',
    description:
      'Politique de livraison Nature & Gîtes : Colissimo / La Poste via StickerGreen, livraison offerte.',
    path: '/livraison',
  },
  privacy: {
    title: 'Politique de confidentialité | Nature & Gîtes',
    description:
      'Politique de confidentialité et protection des données personnelles — site Nature & Gîtes (n-g.fr).',
    path: '/politique-de-confidentialite',
  },
  withdrawal: {
    title: 'Renoncer au contrat ici | Nature & Gîtes',
    description:
      'Exercez votre droit de rétractation en ligne, gratuitement, dans un délai de 14 jours — boutique Nature & Gîtes.',
    path: '/retractation',
  },
  notFound: {
    title: 'Page introuvable | Nature & Gîtes',
    description: 'La page demandée n’existe pas sur le site Nature & Gîtes.',
    path: '/',
    noindex: true,
  },
}

export function absoluteUrl(path = '/') {
  if (!path || path === '/') return `${SITE_URL}/`
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalized}`
}

export function ensureMetaByName(name, content) {
  if (typeof document === 'undefined') return null
  let el = document.querySelector(`meta[name="${name}"]`)
  let created = false
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
    created = true
  }
  const previous = el.getAttribute('content')
  el.setAttribute('content', content)
  return { el, attr: 'content', previous, created }
}

export function ensureMetaByProperty(property, content) {
  if (typeof document === 'undefined') return null
  let el = document.querySelector(`meta[property="${property}"]`)
  let created = false
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
    created = true
  }
  const previous = el.getAttribute('content')
  el.setAttribute('content', content)
  return { el, attr: 'content', previous, created }
}

export function ensureLinkRel(rel, href) {
  if (typeof document === 'undefined') return null
  let el = document.querySelector(`link[rel="${rel}"]`)
  let created = false
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
    created = true
  }
  const previous = el.getAttribute('href')
  el.setAttribute('href', href)
  return { el, attr: 'href', previous, created }
}

export function setJsonLd(id, data) {
  if (typeof document === 'undefined') return null
  const scriptId = `ng-jsonld-${id}`
  let el = document.getElementById(scriptId)
  let created = false
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = scriptId
    document.head.appendChild(el)
    created = true
  }
  const previous = el.textContent
  el.textContent = JSON.stringify(data)
  return { el, previous, created, scriptId }
}

export function restoreHeadMutations(records = []) {
  records.forEach((record) => {
    if (!record) return
    if (record.scriptId) {
      if (record.created) record.el?.remove()
      else if (record.previous != null) record.el.textContent = record.previous
      return
    }
    if (record.created) {
      record.el?.remove()
      return
    }
    if (record.previous == null) record.el?.removeAttribute(record.attr)
    else record.el?.setAttribute(record.attr, record.previous)
  })
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'NGO'],
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: ['N&G', 'Nature and Gites', 'SCIC Nature & Gîtes'],
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
      width: 512,
      height: 512,
    },
    image: DEFAULT_OG_IMAGE,
    description:
      'SCIC Nature & Gîtes : tiers-lieu rural en Nièvre dédié à l’autonomie collective, aux gîtes autonomes, aux vergers agroécologiques et à la forêt durable.',
    email: 'natureandgites@gmail.com',
    telephone: '+33624203832',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '16 Rue du Bout du Puy',
      postalCode: '58330',
      addressLocality: 'Saxi-Bourdon',
      addressRegion: 'Nièvre',
      addressCountry: 'FR',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Nièvre' },
      { '@type': 'AdministrativeArea', name: 'Bourgogne-Franche-Comté' },
      { '@type': 'Country', name: 'France' },
    ],
    knowsAbout: [
      'autonomie collective',
      'tiers-lieu rural',
      'gîtes autonomes',
      'permaculture',
      'agroécologie',
      'forêt durable',
      'SCIC',
      'développement durable',
    ],
    sameAs: SOCIAL_PROFILES,
    founder: {
      '@type': 'Person',
      name: 'Jean Réveillac',
    },
  }
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    description:
      'Site officiel de Nature & Gîtes — autonomie collective en Nièvre, tiers-lieu rural et projet SCIC.',
    inLanguage: 'fr-FR',
    publisher: { '@id': `${SITE_URL}/#organization` },
  }
}

export function buildPlaceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    '@id': `${SITE_URL}/#place`,
    name: 'Nature & Gîtes — Tiers-lieu autonomie collective',
    description:
      'Projet de tiers-lieu rural en Nièvre centré sur l’autonomie collective, l’habitat autonome et l’agroécologie.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '16 Rue du Bout du Puy',
      postalCode: '58330',
      addressLocality: 'Saxi-Bourdon',
      addressRegion: 'Nièvre',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 47.1417,
      longitude: 3.3014,
    },
  }
}

export function buildWebPageSchema({ title, description, path, type = 'WebPage' }) {
  const url = absoluteUrl(path)
  return {
    '@context': 'https://schema.org',
    '@type': type,
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'fr-FR',
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
    },
  }
}

export function buildBreadcrumbSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

/**
 * Applique title, meta, canonical, Open Graph, Twitter, keywords, robots, JSON-LD.
 * @returns {() => void} cleanup
 */
export function applyPageSeo({
  title,
  description,
  path = '/',
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd = [],
  type = 'website',
}) {
  if (typeof document === 'undefined') return () => {}

  const url = absoluteUrl(path)
  const previousTitle = document.title
  document.title = title

  const records = [
    ensureMetaByName('description', description),
    ensureMetaByName('keywords', keywords),
    ensureMetaByName(
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    ),
    ensureMetaByName(
      'googlebot',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    ),
    ensureMetaByName('author', SITE_NAME),
    ensureMetaByName('geo.region', 'FR-58'),
    ensureMetaByName('geo.placename', 'Nièvre'),
    ensureMetaByName('language', 'French'),
    ensureLinkRel('canonical', url),
    ensureMetaByProperty('og:type', type),
    ensureMetaByProperty('og:site_name', SITE_NAME),
    ensureMetaByProperty('og:locale', DEFAULT_LOCALE),
    ensureMetaByProperty('og:title', title),
    ensureMetaByProperty('og:description', description),
    ensureMetaByProperty('og:url', url),
    ensureMetaByProperty('og:image', image),
    ensureMetaByProperty('og:image:width', '512'),
    ensureMetaByProperty('og:image:height', '512'),
    ensureMetaByProperty('og:image:alt', title),
    ensureMetaByName('twitter:card', 'summary_large_image'),
    ensureMetaByName('twitter:title', title),
    ensureMetaByName('twitter:description', description),
    ensureMetaByName('twitter:image', image),
  ]

  const ldRecords = (jsonLd || []).map((data, index) => setJsonLd(`page-${index}`, data))

  return () => {
    document.title = previousTitle
    restoreHeadMutations([...records, ...ldRecords])
  }
}
